<template>
  <v-container fluid class="opciones-cotizacion-list-view">
    <!-- Header -->
    <v-card class="mb-6 header-card" elevation="8">
      <v-card-text class="pa-8">
        <v-row align="center">
          <v-col cols="12" md="8">
            <h1 class="text-h4 font-weight-bold mb-2">
              <v-icon size="40" color="white">mdi-folder-star</v-icon>
              Cotizaciones Pendientes
            </h1>
            <p class="text-h6 mb-0 subtitle">Gestiona las cotizaciones enviadas a tus clientes</p>
          </v-col>
          <v-col cols="12" md="4" class="text-right">
            <v-btn
              color="white"
              variant="outlined"
              size="large"
              prepend-icon="mdi-plus-circle"
              @click="goToCreateOptions"
            >
              Nueva Cotización
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Filtros y Búsqueda -->
    <v-card class="mb-6" elevation="4">
      <v-card-text class="pa-4">
        <v-row align="center">
          <v-col cols="12" md="4">
            <v-text-field
              v-model="filters.search"
              label="Buscar cliente o código"
              placeholder="Buscar..."
              variant="outlined"
              density="compact"
              prepend-inner-icon="mdi-magnify"
              clearable
              hide-details
              @input="debouncedSearch"
            />
          </v-col>

          <v-col cols="12" md="3">
            <v-select
              v-model="filters.estado"
              :items="estadosOptions"
              label="Estado"
              variant="outlined"
              density="compact"
              clearable
              hide-details
              @update:model-value="loadOpciones"
            >
              <template v-slot:selection="{ item }">
                <v-chip :color="getStatusColor(item.value)" size="small">
                  {{ item.title }}
                </v-chip>
              </template>
            </v-select>
          </v-col>

          <v-col cols="12" md="3">
            <v-select
              v-model="filters.tipoEvento"
              :items="tiposEvento"
              item-title="NombreTipo"
              item-value="TipoEventoID"
              label="Tipo de Evento"
              variant="outlined"
              density="compact"
              clearable
              hide-details
              @update:model-value="loadOpciones"
            />
          </v-col>

          <v-col cols="12" md="2">
            <v-btn
              color="primary"
              block
              prepend-icon="mdi-refresh"
              @click="loadOpciones"
            >
              Actualizar
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Loading Skeleton --> 
    <v-row v-if="loading">
      <v-col
        v-for="i in 6"
        :key="`skeleton-${i}`"
        cols="12"
        md="6"
        lg="4"
      >
        <v-skeleton-loader type="card" />
      </v-col>
    </v-row>

    <!-- Tarjetas de Opciones -->
    <v-row v-else-if="opcionesList.length > 0">
      <v-col
        v-for="opcion in opcionesList"
        :key="opcion.OpcionCotizacionID"
        cols="12"
        md="6"
        lg="4"
      >
        <v-card
          :class="['opcion-card', { 'expired': opcion.Expirada }]"
          elevation="3"
          hover
          @click="openDetailsModal(opcion)"
        >
          <!-- Header de la tarjeta -->
          <v-card-title class="pa-4 d-flex align-center justify-space-between bg-grey-lighten-4">
            <div class="d-flex align-center">
              <v-avatar color="primary" size="40" class="mr-3">
                <v-icon color="white">mdi-account</v-icon>
              </v-avatar>
              <div>
                <div class="text-h6 font-weight-bold">{{ opcion.NombreCliente }}</div>
                <div class="text-caption text-grey-darken-1">{{ opcion.CodigoOpcion }}</div>
              </div>
            </div>
            <v-chip
              :color="getStatusColor(opcion.EstadoOpcion)"
              size="small"
              variant="flat"
            >
              <v-icon start size="small">{{ getStatusIcon(opcion.EstadoOpcion) }}</v-icon>
              {{ opcion.EstadoOpcion }}
            </v-chip>
          </v-card-title>

          <!-- Contenido de la tarjeta -->
          <v-card-text class="pa-4">
            <!-- Información del evento -->
            <div class="info-section mb-4">
              <v-row dense>
                <v-col cols="6">
                  <div class="info-item">
                    <v-icon size="small" color="primary" class="mr-1">mdi-party-popper</v-icon>
                    <span class="text-caption text-grey-darken-1">Tipo:</span>
                    <div class="text-body-2 font-weight-medium">{{ opcion.TipoEvento || 'General' }}</div>
                  </div>
                </v-col>
                <v-col cols="6">
                  <div class="info-item">
                    <v-icon size="small" color="primary" class="mr-1">mdi-calendar</v-icon>
                    <span class="text-caption text-grey-darken-1">Fecha:</span>
                    <div class="text-body-2 font-weight-medium">{{ formatDate(opcion.FechaEvento) }}</div>
                  </div>
                </v-col>
                <v-col cols="6">
                  <div class="info-item">
                    <v-icon size="small" color="primary" class="mr-1">mdi-account-group</v-icon>
                    <span class="text-caption text-grey-darken-1">Invitados:</span>
                    <div class="text-body-2 font-weight-medium">{{ opcion.NumeroInvitados }}</div>
                  </div>
                </v-col>
                <v-col cols="6">
                  <div class="info-item">
                    <v-icon size="small" color="primary" class="mr-1">mdi-format-list-numbered</v-icon>
                    <span class="text-caption text-grey-darken-1">Opciones:</span>
                    <div class="text-body-2 font-weight-medium">{{ opcion.NumeroOpciones }}</div>
                  </div>
                </v-col>
              </v-row>
            </div>

            <!-- Expiración -->
            <v-alert
              v-if="opcion.FechaExpiracion && false"
              :type="getExpirationType(opcion)"
              variant="tonal"
              density="compact"
              class="mb-3"
            >
              <div class="d-flex align-center">
                <v-icon start size="small">mdi-clock-outline</v-icon>
                <div class="text-caption">
                  <strong>Expira:</strong> {{ formatDateTime(opcion.FechaExpiracion) }}
                  <span v-if="isExpiringSoon(opcion)" class="ml-1 font-weight-bold">
                    (¡Pronto!)
                  </span>
                </div>
              </div>
            </v-alert>

            <!-- Opción seleccionada -->
            <v-alert
              v-if="opcion.OpcionSeleccionada"
              type="success"
              variant="tonal"
              density="compact"
              class="mb-3"
            >
              <div class="text-caption">
                <v-icon start size="small">mdi-check-circle</v-icon>
                <strong>Seleccionó:</strong> {{ opcion.OpcionSeleccionada }}
                <div v-if="opcion.NumeroCotizacion" class="mt-1">
                  <strong>Cotización:</strong> {{ opcion.NumeroCotizacion }}
                </div>
              </div>
            </v-alert>

            <!-- Fecha de creación -->
            <div class="text-caption text-grey-darken-1 text-center">
              <v-icon size="small">mdi-calendar-clock</v-icon>
              Creada el {{ formatDateTime(opcion.FechaCreacion) }}
            </div>
          </v-card-text>

          <!-- Acciones rápidas -->
          <v-card-actions class="pa-4 pt-0">
            <v-row dense>
                <v-col cols="12">
                    <v-btn
                    color="primary"
                    variant="tonal"
                    size="small"
                    block
                    prepend-icon="mdi-eye"
                    @click.stop="openDetailsModal(opcion)"
                    >
                    Ver Detalles
                    </v-btn>
                </v-col>
                <v-col cols="12">
                    <v-btn
                    color="error"
                    variant="text"
                    size="small" 
                    block
                    prepend-icon="mdi-delete"
                    @click.stop="deleteOption(opcion)"
                    >
                    Eliminar Opción
                    </v-btn>
                </v-col>
            </v-row>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <!-- Empty State -->
    <v-card v-else class="text-center pa-10" elevation="2">
      <v-icon size="80" color="grey-lighten-1">mdi-folder-open</v-icon>
      <h3 class="text-h5 text-grey mt-4 mb-2">No hay opciones de cotización</h3>
      <p class="text-body-1 text-grey-darken-1 mb-4">
        Crea nuevas opciones para que tus clientes elijan la mejor alternativa
      </p>
      <v-btn color="primary" size="large" prepend-icon="mdi-plus" @click="goToCreateOptions">
        Crear Opciones de Cotización
      </v-btn>
    </v-card>

    <!-- Modal de Detalles -->
    <v-dialog
      v-model="detailsModal"
      max-width="1400"
      scrollable
      persistent
    >
      <v-card v-if="selectedOpcion">
        <!-- Header del Modal -->
        <v-toolbar color="primary" dark>
          <v-toolbar-title>
            <v-icon start>mdi-folder-information</v-icon>
            {{ selectedOpcion.CodigoOpcion }} - {{ selectedOpcion.NombreCliente }}
          </v-toolbar-title>
          <v-spacer />
          <v-chip
            :color="getStatusColor(selectedOpcion.EstadoOpcion)"
            variant="flat"
            class="mr-2"
          >
            {{ selectedOpcion.EstadoOpcion }}
          </v-chip>
          <v-btn icon @click="closeDetailsModal">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <!-- Contenido del Modal -->
        <v-card-text class="pa-6" style="max-height: 70vh;">
          <v-row>
            <!-- Columna Izquierda: Información General -->
            <v-col cols="12" md="4">
              <!-- Info del Cliente -->
              <v-card variant="outlined" class="mb-4">
                <v-card-title class="text-subtitle-1 bg-grey-lighten-4 pa-3">
                  <v-icon start color="primary">mdi-account-circle</v-icon>
                  Información del Cliente
                </v-card-title>
                <v-card-text class="pa-4">
                  <v-list density="compact">
                    <v-list-item>
                      <v-list-item-title class="text-caption text-grey-darken-1">Cliente</v-list-item-title>
                      <v-list-item-subtitle class="text-body-2 font-weight-bold">
                        {{ selectedOpcion.NombreCliente }}
                      </v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-title class="text-caption text-grey-darken-1">Tipo de Evento</v-list-item-title>
                      <v-list-item-subtitle class="text-body-2">
                        {{ selectedOpcion.TipoEvento || 'General' }}
                      </v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-title class="text-caption text-grey-darken-1">Fecha del Evento</v-list-item-title>
                      <v-list-item-subtitle class="text-body-2">
                        {{ formatDate(selectedOpcion.FechaEvento) }}
                      </v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-title class="text-caption text-grey-darken-1">Invitados</v-list-item-title>
                      <v-list-item-subtitle class="text-body-2">
                        {{ selectedOpcion.NumeroInvitados }}
                      </v-list-item-subtitle>
                    </v-list-item>
                  </v-list>
                </v-card-text>
              </v-card>

              <!-- Enlace del Cliente -->
              <v-card v-if="false" variant="outlined" class="mb-4">
                <v-card-title class="text-subtitle-1 bg-grey-lighten-4 pa-3">
                  <v-icon start color="primary">mdi-link</v-icon>
                  Enlace para el Cliente
                </v-card-title>
                <v-card-text class="pa-4">
                  <v-text-field
                    :model-value="getClientLink(selectedOpcion)"
                    readonly
                    variant="outlined"
                    density="compact"
                    hide-details
                  >
                    <template v-slot:append>
                      <v-btn
                        icon="mdi-content-copy"
                        variant="text"
                        size="small"
                        @click="copyToClipboard(getClientLink(selectedOpcion))"
                      />
                    </template>
                  </v-text-field>
                  <v-btn
                    color="primary"
                    variant="tonal"
                    block
                    class="mt-3"
                    prepend-icon="mdi-email"
                    size="small"
                    @click="sendByEmail(selectedOpcion)"
                  >
                    Enviar por Email
                  </v-btn>
                  <v-btn
                    color="success"
                    variant="tonal"
                    block
                    class="mt-2"
                    prepend-icon="mdi-whatsapp"
                    size="small"
                    @click="sendByWhatsApp(selectedOpcion)"
                  >
                    Enviar por WhatsApp
                  </v-btn>
                </v-card-text>
              </v-card>

              <!-- Estado y Fechas -->
              <v-card variant="outlined">
                <v-card-title class="text-subtitle-1 bg-grey-lighten-4 pa-3">
                  <v-icon start color="primary">mdi-information</v-icon>
                  Estado
                </v-card-title>
                <v-card-text class="pa-4">
                  <v-list density="compact">
                    <v-list-item>
                      <v-list-item-title class="text-caption text-grey-darken-1">Estado</v-list-item-title>
                      <v-list-item-subtitle>
                        <v-chip :color="getStatusColor(selectedOpcion.EstadoOpcion)" size="small">
                          {{ selectedOpcion.EstadoOpcion }}
                        </v-chip>
                      </v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-title class="text-caption text-grey-darken-1">Creada</v-list-item-title>
                      <v-list-item-subtitle class="text-body-2">
                        {{ formatDateTime(selectedOpcion.FechaCreacion) }}
                      </v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item v-if="selectedOpcion.FechaExpiracion">
                      <v-list-item-title class="text-caption text-grey-darken-1">Expira</v-list-item-title>
                      <v-list-item-subtitle class="text-body-2">
                        {{ formatDateTime(selectedOpcion.FechaExpiracion) }}
                      </v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item v-if="selectedOpcion.OpcionSeleccionada">
                      <v-list-item-title class="text-caption text-grey-darken-1">Opción Seleccionada</v-list-item-title>
                      <v-list-item-subtitle class="text-body-2 text-success font-weight-bold">
                        {{ selectedOpcion.OpcionSeleccionada }}
                      </v-list-item-subtitle>
                    </v-list-item>
                  </v-list>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Columna Derecha: Opciones -->
            <v-col cols="12" md="8">
              <h3 class="text-h6 mb-4">
                <v-icon color="primary" class="mr-2">mdi-format-list-checks</v-icon>
                Opciones Presentadas ({{ detailsOptions.length }})
              </h3>

              <!-- Loading de opciones -->
              <div v-if="loadingDetails" class="text-center pa-6">
                <v-progress-circular
                  indeterminate
                  color="primary"
                  size="64"
                />
                <p class="text-body-1 text-grey mt-4">Cargando opciones...</p>
              </div>

              <!-- Listado de opciones -->
              <v-row v-else>
                <v-col
                  v-for="option in detailsOptions"
                  :key="option.DetalleOpcionID"
                  cols="12"
                >
                  <v-card
                    :class="['option-detail-card', { 'selected-option': option.EsSeleccionada }]"
                    elevation="2"
                  >
                    <!-- Header de la opción -->
                    <v-card-title class="pa-4 bg-grey-lighten-4 d-flex align-center justify-space-between">
                      <div class="d-flex align-center">
                        <v-avatar
                          :color="option.EsSeleccionada ? 'success' : 'primary'"
                          size="40"
                          class="mr-3"
                        >
                          <span class="text-h6 font-weight-bold">{{ option.NumeroOpcion }}</span>
                        </v-avatar>
                        <div>
                          <div class="text-h6 font-weight-bold">{{ option.NombreOpcion }}</div>
                          <div v-if="option.DescripcionOpcion" class="text-caption text-grey-darken-1">
                            {{ option.DescripcionOpcion }}
                          </div>
                        </div>
                      </div>
                      <v-chip
                        v-if="option.EsSeleccionada"
                        color="success"
                        size="small"
                        prepend-icon="mdi-check-circle"
                      >
                        Seleccionada
                      </v-chip>
                    </v-card-title>

                    <!-- Contenido de la opción -->
                    <v-card-text class="pa-4">
                      <v-row>
                        <!-- Items incluidos -->
                        <v-col cols="12" md="7">
                          <div class="text-subtitle-2 mb-2">Items incluidos:</div>
                          <div class="items-summary">
                            <v-chip
                              v-for="(service, index) in getServicesSummary(option.DetalleOpcionID)"
                              :key="index"
                              size="small"
                              variant="outlined"
                              color="primary"
                              class="ma-1"
                            >
                              {{ service.name }} ({{ service.count }})
                            </v-chip>
                          </div>
                          <div class="text-caption text-grey-darken-1 mt-2">
                            Total: {{ getOptionItemsCount(option.DetalleOpcionID) }} unidades
                          </div>
                        </v-col>

                        <!-- Precios -->
                        <v-col cols="12" md="5">
                          <v-card variant="outlined" class="pricing-card">
                            <v-card-text class="pa-3">
                              <div class="d-flex justify-space-between mb-2">
                                <span class="text-body-2">Subtotal:</span>
                                <span class="text-body-2 font-weight-medium">
                                  {{ formatCurrency(option.Subtotal) }}
                                </span>
                              </div>
                              <div v-if="option.MontoDescuento > 0" class="d-flex justify-space-between mb-2 text-red">
                                <span class="text-body-2">Descuento:</span>
                                <span class="text-body-2">-{{ formatCurrency(option.MontoDescuento) }}</span>
                              </div>
                              <v-divider class="my-2" />
                              <div class="d-flex justify-space-between">
                                <span class="text-h6 font-weight-bold">Total:</span>
                                <span class="text-h6 font-weight-bold text-success">
                                  {{ formatCurrency(option.TotalConDescuento) }}
                                </span>
                              </div>
                            </v-card-text>
                          </v-card>
                        </v-col>
                      </v-row>
                      <!-- Botón ver detalles completos y seleccionar -->
                      <v-btn
                        variant="outlined"
                        color="primary"
                        size="small"
                        class="mt-2"
                        @click="selectOption(option)"
                      >
                        <v-icon start>mdi-check</v-icon>
                        Seleccionar esta opción
                      </v-btn>
                      <v-btn
                        variant="text"
                        color="primary"
                        size="small"
                        class="mt-2"
                        @click="viewFullOptionDetails(option)"
                      >
                        <v-icon start>mdi-eye</v-icon>
                        Ver detalles completos
                      </v-btn>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-card-text>

        <!-- Acciones del Modal -->
        <v-divider />
        <v-card-actions class="pa-6 bg-grey-lighten-5">
          <v-btn
            color="success"
            variant="flat"
            prepend-icon="mdi-printer"
            @click="generatePdf()"
          >
            Imprimir opciónes
          </v-btn>
          <v-btn
            v-if="selectedOpcion.EstadoOpcion === 'Pendiente'"
            color="warning"
            variant="tonal"
            prepend-icon="mdi-clock-plus"
            @click="extendExpiration(selectedOpcion)"
          >
            Extender Expiración
          </v-btn>
          <v-spacer />
          <v-btn
            color="grey"
            variant="text"
            @click="closeDetailsModal"
          >
            Cerrar
          </v-btn>
          <v-btn
            v-if="selectedOpcion.EstadoOpcion === 'Seleccionada' && selectedOpcion.CotizacionGeneradaID"
            color="success"
            variant="flat"
            size="large"
            prepend-icon="mdi-file-document-check"
            @click="goToCotizacion(selectedOpcion.CotizacionGeneradaID)"
          >
            Ver Cotización Final
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal: Detalles Completos de una Opción -->
    <v-dialog v-model="fullDetailsModal" max-width="1000" scrollable>
      <v-card v-if="selectedFullOption">
        <v-toolbar color="secondary" dark>
          <v-toolbar-title>
            <v-icon start>mdi-format-list-bulleted-square</v-icon>
            {{ selectedFullOption.NombreOpcion }} - Detalles Completos
          </v-toolbar-title>
          <v-spacer />
          <v-btn icon @click="fullDetailsModal = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-6" style="max-height: 600px;">
          <!-- Items agrupados por servicio y sección -->
          <div
            v-for="(service, serviceName) in getItemsGroupedByService(selectedFullOption.DetalleOpcionID)"
            :key="serviceName"
            class="mb-6"
          >
            <h3 class="text-h6 mb-3 d-flex align-center">
              <v-icon color="primary" class="mr-2">mdi-folder</v-icon>
              {{ serviceName }}
            </h3>

            <div
              v-for="(section, sectionName) in service"
              :key="sectionName"
              class="ml-4 mb-4"
            >
              <h4 class="text-subtitle-1 mb-2 d-flex align-center">
                <v-icon size="small" color="secondary" class="mr-2">mdi-folder-outline</v-icon>
                {{ sectionName }}
              </h4>

              <v-list density="compact" class="ml-4">
                <v-list-item
                  v-for="item in section"
                  :key="item.ItemOpcionID"
                  class="mb-2"
                >
                  <template v-slot:prepend>
                    <v-icon color="secondary" size="small" class="mt-1">mdi-cube-outline</v-icon>
                  </template>
                  <v-list-item-title>
                    <v-chip
                      color="primary"
                      size="x-small"
                      class="mr-2 font-weight-bold"
                    >{{ item.Cantidad }}x</v-chip>
                    {{ item.NombreItem }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    <span class="text-caption text-grey-darken-1">
                      {{ formatCurrency(item.PrecioUnitario) }} c/u
                      <span v-if="item.Cantidad > 1"> × {{ item.Cantidad }} = {{ formatCurrency(item.Subtotal) }}</span>
                    </span>
                    <span v-if="item.Descripcion" class="ml-2 text-caption">— {{ item.Descripcion }}</span>
                  </v-list-item-subtitle>
                  <template v-slot:append>
                    <span class="text-body-2 font-weight-bold">
                      {{ formatCurrency(item.Subtotal) }}
                    </span>
                  </template>
                </v-list-item>
              </v-list>
            </div>
          </div>
        </v-card-text>

        <v-divider />
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn @click="fullDetailsModal = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      timeout="3000"
      location="top"
    >
      {{ snackbar.message }}
    </v-snackbar>

<!-- Diálogo de recarga -->
<v-dialog v-model="reloadDialog" max-width="420" persistent>
  <v-card>
    <v-card-title class="text-h6 pt-5 px-6">
      <v-icon color="primary" class="mr-2">mdi-refresh</v-icon>
      Actualizar opciones
    </v-card-title>
    <v-card-text class="px-6">
      Acabas de crear nuevas opciones de cotización. ¿Deseas recargar la lista para ver las opciones actualizadas?
    </v-card-text>
    <v-card-actions class="pa-4">
      <v-spacer />
      <v-btn variant="text" @click="reloadDialog = false">No, mantener</v-btn>
      <v-btn color="primary" variant="elevated" @click="confirmReload">Sí, actualizar</v-btn>
    </v-card-actions>
  </v-card>
</v-dialog>
  </v-container>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, onActivated } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { generatePDFOptions as generatePDFUtilOptions, type PdfData } from '@/utils/pdfGenerator-opciones'
import { loadImageBase64 } from '@/utils/constantes'

defineOptions({
  name: 'cotizacionOpciones'
})

const router = useRouter()
const store = useStore()

const route = useRoute()  // agregar junto a router y store

const reloadDialog = ref(false)

// ============================================================================
// STATE
// ============================================================================

const saving = ref(false)

const loading = ref(false)
const loadingDetails = ref(false)
const opcionesList = ref<any[]>([])
const tiposEvento = ref<any[]>([])

const filters = ref({
  search: '',
  estado: null,
  tipoEvento: null
})

const estadosOptions = [
  { title: 'Pendiente', value: 'Pendiente' },
  { title: 'Seleccionada', value: 'Seleccionada' },
  { title: 'Rechazada', value: 'Rechazada' },
  { title: 'Expirada', value: 'Expirada' }
]

const detailsModal = ref(false)
const fullDetailsModal = ref(false)
const selectedOpcion = ref<any>(null)
const selectedFullOption = ref<any>(null)
const detailsOptions = ref<any[]>([])
const detailsOption = ref<any>(null)
const detailsItems = ref<any[]>([])

const snackbar = ref({
  show: false,
  message: '',
  color: 'success'
})

const toast = ref({
  show: false,
  message: '',
  type: 'success'
})

let searchTimeout: any = null

// ============================================================================
// COMPUTED
// ============================================================================

const filteredOpciones = computed(() => {
  return opcionesList.value
})

// ============================================================================
// METHODS
// ============================================================================

async function loadOpciones() {
  try {
    loading.value = true
    await store.dispatch('Cotizacion/getOpcionesCotizacionList', filters.value)
    opcionesList.value = store.state.Cotizacion.OpcionesCotizacionList?.data?.data || []
  } catch (error) {
    showSnackbar('Error al cargar las opciones', 'error')
  } finally {
    loading.value = false
  }
}

async function loadTiposEvento() {
  try {
    await store.dispatch('Cotizacion/getTiposEvento')
    tiposEvento.value = store.state.Cotizacion.TiposEvento?.data?.data || []
  } catch (error) {
    console.error('Error cargando tipos de evento:', error)
  }
}

function debouncedSearch() {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    loadOpciones()
  }, 500)
}

async function openDetailsModal(opcion: any) {
  selectedOpcion.value = opcion
  detailsModal.value = true
  
  // Cargar detalles de las opciones
  try {
    loadingDetails.value = true
    
    // Aquí deberías tener un endpoint específico para obtener los detalles
    // Por ahora simulamos con los datos que tienes
    await store.dispatch('Cotizacion/getOpcionCotizacionDetails', opcion.OpcionCotizacionID)
    detailsOptions.value =store.state.Cotizacion.OpcionCotizacionDetails?.data?.data?.options || []
    detailsOption.value =store.state.Cotizacion.OpcionCotizacionDetails?.data?.data?.opcion || []
    detailsItems.value =store.state.Cotizacion.OpcionCotizacionDetails?.data?.data?.items || []
    
    
  } catch (error) {
    showSnackbar('Error al cargar detalles', 'error')
  } finally {
    loadingDetails.value = false
  }
}

function closeDetailsModal() {
  detailsModal.value = false
  selectedOpcion.value = null
  detailsOptions.value = []
  detailsItems.value = []
}

function viewFullOptionDetails(option: any) {
  selectedFullOption.value = option
  fullDetailsModal.value = true
}

function getClientLink(opcion: any) {
  const baseUrl = window.location.origin
  return `${baseUrl}/opciones-cotizacion/${opcion.TokenAcceso}`
}

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text)
  showSnackbar('Enlace copiado al portapapeles', 'success')
}

function sendByEmail(opcion: any) {
  const link = getClientLink(opcion)
  const subject = `Opciones de Cotización - ${opcion.CodigoOpcion}`
  const body = `Estimado/a ${opcion.NombreCliente},\n\nHemos preparado ${opcion.NumeroOpciones} opciones para su evento.\n\nPuede revisarlas en el siguiente enlace:\n${link}\n\nVálido hasta: ${formatDateTime(opcion.FechaExpiracion)}\n\nSaludos cordiales.`
  
  window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
}

function sendByWhatsApp(opcion: any) {
  const link = getClientLink(opcion)
  const message = `¡Hola ${opcion.NombreCliente}! 👋\n\nHemos preparado ${opcion.NumeroOpciones} opciones especiales para tu evento.\n\n🔗 Revísalas aquí: ${link}\n\n⏰ Válido hasta: ${formatDateTime(opcion.FechaExpiracion)}\n\n¡Esperamos tu respuesta!`
  
  window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank')
}

function goToCotizacion(cotizacionID: number) {
  router.push(`/cotizacion/${cotizacionID}`)
}

function goToCreateOptions() {
  router.push('/cotizaciones/new')
}

async function extendExpiration(opcion: any) {
  try {
    await store.dispatch('Cotizacion/extenderExpiracionOpcion', {
      opcionCotizacionID: opcion.OpcionCotizacionID,
      dias: 7
    })
    showSnackbar('Expiración extendida 7 días', 'success')
    loadOpciones()
    closeDetailsModal()
  } catch (error) {
    showSnackbar('Error al extender expiración', 'error')
  }
}

function getServicesSummary(detalleOpcionID: number) {
  const items = detailsItems.value.filter(item => item.DetalleOpcionID === detalleOpcionID)
  const services = new Map<string, number>()

  items.forEach(item => {
    const count = services.get(item.NombreServicio) || 0
    services.set(item.NombreServicio, count + (item.Cantidad || 1))
  })

  return Array.from(services.entries()).map(([name, count]) => ({ name, count }))
}

function getOptionItemsCount(detalleOpcionID: number) {
  return detailsItems.value
    .filter(item => item.DetalleOpcionID === detalleOpcionID)
    .reduce((sum: number, item: any) => sum + (item.Cantidad || 1), 0)
}

function getItemsGroupedByService(detalleOpcionID: number) {
  const items = detailsItems.value.filter(item => item.DetalleOpcionID === detalleOpcionID)
  const grouped: any = {}
  
  items.forEach(item => {
    if (!grouped[item.NombreServicio]) {
      grouped[item.NombreServicio] = {}
    }
    if (!grouped[item.NombreServicio][item.NombreSeccion]) {
      grouped[item.NombreServicio][item.NombreSeccion] = []
    }
    grouped[item.NombreServicio][item.NombreSeccion].push(item)
  })
  
  return grouped
}

function formatDate(dateString: string) {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleDateString('es-CO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

function formatDateTime(dateString: string) {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleString('es-CO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function formatCurrency(value: number | string) {
  if (typeof value === 'string') {
    value = parseFloat(value)
  }
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0
  }).format(value || 0)
}

function getStatusColor(status: string) {
  const colors: any = {
    'Pendiente': 'warning',
    'Seleccionada': 'success',
    'Rechazada': 'error',
    'Expirada': 'grey'
  }
  return colors[status] || 'grey'
}

function getStatusIcon(status: string) {
  const icons: any = {
    'Pendiente': 'mdi-clock-outline',
    'Seleccionada': 'mdi-check-circle',
    'Rechazada': 'mdi-close-circle',
    'Expirada': 'mdi-timer-off'
  }
  return icons[status] || 'mdi-help-circle'
}

function getExpirationType(opcion: any) {
  if (opcion.Expirada) return 'error'
  if (isExpiringSoon(opcion)) return 'warning'
  return 'info'
}

function isExpiringSoon(opcion: any) {
  if (!opcion.FechaExpiracion) return false
  const expiration = new Date(opcion.FechaExpiracion)
  const now = new Date()
  const diffHours = (expiration.getTime() - now.getTime()) / (1000 * 60 * 60)
  return diffHours > 0 && diffHours < 48
}

function showSnackbar(message: string, color = 'success') {
  snackbar.value = { show: true, message, color }
}

function selectOption(option: any) {
  saveQuotation(option)
}

function getPendingAmount(option: any) {
  return Number.parseFloat(option.TotalConDescuento) // - Number.parseFloat(option.TotalPagado)
}

function getItemsByService(detalleOpcionID: number, option: any) {
  const items = detailsItems.value.filter(item => item.DetalleOpcionID === detalleOpcionID)
  return items
}

  async function saveQuotation(option: any) {
    if(!confirm('¿Está seguro de guardar esta cotización? esto eliminara la cotización de la lista de opciones.')) return
        // TODO: agregar un modal para confirmar la cotización
    try {
      const itemsGroupedByService = getItemsByService(option.DetalleOpcionID, option) 
      saving.value = true
      await store.dispatch('Cotizacion/fetchCatalogo')

      const pdfDataOption = JSON.parse(detailsOption.value.PdfData) || []
      const dataOption = pdfDataOption.selectedItems.find((item: any) => item.nombre === option.NombreOpcion)
      const { servicios } = store.state.Cotizacion.Catalogo.data.data
      const pdfData: PdfData = {
        nombreCliente: selectedOpcion.value.NombreCliente,
        tipoEvento: selectedOpcion.value.TipoEvento,
        selectedItems: dataOption.selectedItems,
        catalogoServicios: servicios,
        eventDate: selectedOpcion.value.FechaEvento,
        guestCount: detailsOption.value.NumeroInvitados,
        discountPercent: detailsOption.value.PorcentajeDescuento,
        discountAmount: Number.parseFloat(option.MontoDescuento),
        subtotal: Number.parseFloat(option.Subtotal),
        totalWithDiscount: Number.parseFloat(option.TotalConDescuento),
        payments: [{ amount: 0 }],
        pendingAmount: getPendingAmount(option),
        observations: '',
        backgroundRespage: await loadImageBase64('/pdf/bk_pdf.png'),
        backgroundHeader: await loadImageBase64('/pdf/header.png'),
        signatureImage: await loadImageBase64('/pdf/firma.png')
      }
      const payload = {
        NombreCliente: selectedOpcion.value.NombreCliente,
        TipoEventoID: detailsOption.value.TipoEventoID,
        FechaEvento: selectedOpcion.value.FechaEvento,
        NumeroInvitados: selectedOpcion.value.NumeroInvitados,
        PorcentajeDescuento: Number(detailsOption.value.PorcentajeDescuento),
        Observaciones: selectedOpcion.value.Observaciones || '',
        PdfData: JSON.stringify(pdfData),
        items: itemsGroupedByService.map(item => ({
          itemID: item.ItemID,
          cantidad: item.Cantidad,
          observaciones: item.Observaciones
        })),
        abonos: []
      }
      await store.dispatch('Cotizacion/createCotizacion', payload)
      const result = store.state.Cotizacion.result
      if (result.success) {
        await store.dispatch('Cotizacion/deleteCotizacion', option.OpcionCotizacionID)
      }
      await loadOpciones() 
      fullDetailsModal.value = false
      detailsModal.value = false
      loadingDetails.value = false
      goToList()
    } catch (error: any) {
      console.error('Error guardando cotización:', error)
      showToast(error.response?.data?.message || 'Error al guardar la cotización', 'error')
    } finally {
      saving.value = false
    }
  }

  function showToast(message: string, type = 'success') {
  toast.value = { show: true, message, type }
}
function goToList() {
  router.push({ path: '/cotizaciones/list', query: { from: 'opciones' } })
}
async function generatePdf() {
  saving.value = true
  try {
    
    const pdfDataOption = JSON.parse(detailsOption.value.PdfData) || []
    const pdfData: PdfData = {
      nombreCliente: pdfDataOption.nombreCliente,
      tipoEvento: pdfDataOption.tipoEvento || '',
      eventDate: pdfDataOption.eventDate,
      guestCount: pdfDataOption.guestCount || 0,
      discountPercent: pdfDataOption.discountPercent || 0,
      observations: pdfDataOption.observations || '',
      opciones: pdfDataOption.opciones,  
      catalogoServicios: pdfDataOption.catalogoServicios,
      payments: pdfDataOption.payments,
      pendingAmount: pdfDataOption.pendingAmount,
      selectedItems: [],
      subtotal: pdfDataOption.subtotal,
      discountAmount: pdfDataOption.discountAmount,
      totalWithDiscount: pdfDataOption.totalWithDiscount,
      backgroundRespage: pdfDataOption.backgroundRespage,
      backgroundHeader: pdfDataOption.backgroundHeader,
      signatureImage: pdfDataOption.signatureImage
    }
    
    await generatePDFUtilOptions(pdfData)
    showToast('✅ PDF generado exitosamente')
  } catch (error) {
    console.error('Error generando PDF:', error)
    showToast('Error al generar el PDF', 'error')
  } finally {
    saving.value = false
  }
}

async function deleteOption(option: any) {
    if(!confirm('¿Está seguro de eliminar la opción?')) return
  try {
      await store.dispatch('Cotizacion/eliminarOpcionCotizacion', option)
      const result = store.state.Cotizacion.result
      if (result.success) {
        loadOpciones()
      } else {
        showToast('Error al eliminar la opción', 'error')
      }
  } catch (error) {
    showToast('Error al eliminar la opción', 'error')
  }
}

// ============================================================================
// LIFECYCLE
// ============================================================================

onMounted(() => {
  loadOpciones()
  loadTiposEvento()
})

onActivated(() => {
  // Si viene desde cotizacionNew, preguntar si quiere recargar
  if (route.query.from === 'new') {
    reloadDialog.value = true
    // Limpiar el query para que no vuelva a dispararse si navega entre tabs
    router.replace({ query: {} })
  }
})
function confirmReload() {
  reloadDialog.value = false
  loadOpciones()
}
</script>

<style scoped>
.opciones-cotizacion-list-view {
  background: linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%);
  min-height: 100vh;
  padding-bottom: 50px;
}

.header-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
}

.opcion-card {
  transition: all 0.3s ease;
  cursor: pointer;
  border-left: 4px solid transparent;
}

.opcion-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15) !important;
  border-left-color: #667eea;
}

.opcion-card.expired {
  opacity: 0.7;
  border-left-color: #9e9e9e;
}

.info-item {
  margin-bottom: 8px;
}

.option-detail-card {
  transition: all 0.3s ease;
}

.option-detail-card.selected-option {
  border: 2px solid #4caf50;
  background: linear-gradient(135deg, #f0f9f0 0%, #ffffff 100%);
}

.items-summary {
  max-height: 120px;
  overflow-y: auto;
}

.pricing-card {
  background: rgba(0, 0, 0, 0.02);
}
</style>